/**
 * NotificationPreferences — Per-user notification preferences (Settings > App Settings).
 * Contains: delivery methods, transfer notifications, waste log notifications.
 */
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Switch } from "../ui/switch";
import { Bell, Envelope, ArrowsLeftRight, Trash } from "@phosphor-icons/react";

export function NotificationPreferences({ notifPrefs, savingNotifPrefs, onChange }) {
  return (
    <Card className="border rounded-xl">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-lg tracking-wide flex items-center gap-2">
          <Bell size={20} weight="duotone" className="text-primary" />
          MY NOTIFICATION PREFERENCES
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Delivery Methods */}
        <div className="p-4 bg-muted/30 rounded-lg space-y-3">
          <p className="font-heading tracking-wide text-sm">DELIVERY METHODS</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center justify-between p-3 bg-white rounded-lg border cursor-pointer">
              <span className="flex items-center gap-2 text-sm">
                <Bell size={16} className="text-blue-500" />
                In-App Notifications
              </span>
              <Switch
                data-testid="notif-in-app-toggle"
                checked={notifPrefs.in_app_enabled}
                onCheckedChange={(val) => onChange("in_app_enabled", val)}
                disabled={savingNotifPrefs}
              />
            </label>
            <label className="flex items-center justify-between p-3 bg-white rounded-lg border cursor-pointer">
              <span className="flex items-center gap-2 text-sm">
                <Envelope size={16} className="text-green-500" />
                Email Notifications
              </span>
              <Switch
                data-testid="notif-email-toggle"
                checked={notifPrefs.email_enabled}
                onCheckedChange={(val) => onChange("email_enabled", val)}
                disabled={savingNotifPrefs}
              />
            </label>
          </div>
        </div>

        {/* Transfer Notifications */}
        <div className="p-4 bg-muted/30 rounded-lg space-y-3">
          <p className="font-heading tracking-wide text-sm flex items-center gap-2">
            <ArrowsLeftRight size={16} className="text-blue-500" />
            TRANSFER NOTIFICATIONS
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center justify-between p-3 bg-white rounded-lg border cursor-pointer">
              <span className="text-sm">New transfer requests</span>
              <Switch
                data-testid="notif-transfer-created"
                checked={notifPrefs.transfer_created}
                onCheckedChange={(val) => onChange("transfer_created", val)}
                disabled={savingNotifPrefs}
              />
            </label>
            <label className="flex items-center justify-between p-3 bg-white rounded-lg border cursor-pointer">
              <span className="text-sm">Transfer approved</span>
              <Switch
                data-testid="notif-transfer-approved"
                checked={notifPrefs.transfer_approved}
                onCheckedChange={(val) => onChange("transfer_approved", val)}
                disabled={savingNotifPrefs}
              />
            </label>
            <label className="flex items-center justify-between p-3 bg-white rounded-lg border cursor-pointer">
              <span className="text-sm">Transfer rejected</span>
              <Switch
                data-testid="notif-transfer-rejected"
                checked={notifPrefs.transfer_rejected}
                onCheckedChange={(val) => onChange("transfer_rejected", val)}
                disabled={savingNotifPrefs}
              />
            </label>
            <label className="flex items-center justify-between p-3 bg-white rounded-lg border cursor-pointer">
              <span className="text-sm">Incoming transfers</span>
              <Switch
                data-testid="notif-transfer-received"
                checked={notifPrefs.transfer_received}
                onCheckedChange={(val) => onChange("transfer_received", val)}
                disabled={savingNotifPrefs}
              />
            </label>
          </div>
        </div>

        {/* Waste Log Notifications */}
        <div className="p-4 bg-muted/30 rounded-lg space-y-3">
          <p className="font-heading tracking-wide text-sm flex items-center gap-2">
            <Trash size={16} className="text-orange-500" />
            WASTE LOG NOTIFICATIONS
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center justify-between p-3 bg-white rounded-lg border cursor-pointer">
              <span className="text-sm">Daily waste reminder</span>
              <Switch
                data-testid="notif-waste-daily"
                checked={notifPrefs.waste_log_daily_reminder}
                onCheckedChange={(val) => onChange("waste_log_daily_reminder", val)}
                disabled={savingNotifPrefs}
              />
            </label>
            <label className="flex items-center justify-between p-3 bg-white rounded-lg border cursor-pointer">
              <span className="text-sm">Weekly waste summary</span>
              <Switch
                data-testid="notif-waste-weekly"
                checked={notifPrefs.waste_log_weekly_summary}
                onCheckedChange={(val) => onChange("waste_log_weekly_summary", val)}
                disabled={savingNotifPrefs}
              />
            </label>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
